import React, { useEffect, useState } from 'react';
import { Button, Paper, TextField, Typography, Box, Checkbox, FormControlLabel, Link } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { persistAuthSession } from '../helpers/authSession';
import { refreshSessionContext } from '../helpers/sessionContext';
import { startSubscriptionCheckoutAfterMigrate } from '../components/subscription/subscriptionPlanMigration';
import RegistrationModal from '../components/RegistrationModal';

const LOGIN_LOCK_KEY = 'acompanha_login_lock';
const MAX_LOCAL_FAILURES = 5;
const LOCAL_LOCK_MS = 5 * 60 * 1000;

function readLoginLock() {
    try {
        const raw = sessionStorage.getItem(LOGIN_LOCK_KEY);
        if (!raw) {
            return { failures: 0, lockedUntil: 0 };
        }
        const parsed = JSON.parse(raw);
        return {
            failures: Number(parsed.failures) || 0,
            lockedUntil: Number(parsed.lockedUntil) || 0,
        };
    } catch {
        return { failures: 0, lockedUntil: 0 };
    }
}

function writeLoginLock(state) {
    sessionStorage.setItem(LOGIN_LOCK_KEY, JSON.stringify(state));
}

function clearLoginLock() {
    sessionStorage.removeItem(LOGIN_LOCK_KEY);
}

function remainingLockMs(lockedUntil) {
    return Math.max(0, lockedUntil - Date.now());
}

function LoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const wantCheckout = searchParams.get('checkout') === '1';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
    const [lockedUntil, setLockedUntil] = useState(() => readLoginLock().lockedUntil);
    const [lockTick, setLockTick] = useState(0);

    const lockRemainingMs = remainingLockMs(lockedUntil);
    const isLocked = lockRemainingMs > 0;

    useEffect(() => {
        if (!isLocked) {
            return undefined;
        }
        const timer = window.setInterval(() => {
            const remaining = remainingLockMs(lockedUntil);
            if (remaining <= 0) {
                const current = readLoginLock();
                writeLoginLock({ ...current, lockedUntil: 0, failures: 0 });
                setLockedUntil(0);
                setError('');
            }
            setLockTick((value) => value + 1);
        }, 1000);
        return () => window.clearInterval(timer);
    }, [isLocked, lockedUntil]);

    const registerLocalFailure = () => {
        const current = readLoginLock();
        const failures = current.failures + 1;
        if (failures >= MAX_LOCAL_FAILURES) {
            const until = Date.now() + LOCAL_LOCK_MS;
            writeLoginLock({ failures, lockedUntil: until });
            setLockedUntil(until);
            setError('Muitas tentativas neste navegador. Aguarde alguns minutos antes de tentar de novo.');
            return;
        }
        writeLoginLock({ failures, lockedUntil: current.lockedUntil || 0 });
        setError('Credenciais inválidas. Por favor, tente novamente.');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (isLocked) {
            const seconds = Math.ceil(lockRemainingMs / 1000);
            setError(`Login temporariamente bloqueado. Tente novamente em ${seconds}s.`);
            return;
        }
        setIsSubmitting(true);
        setError('');
        try {
            const response = await api.post('auth/login', {
                email,
                password,
                rememberMe,
            });
            clearLoginLock();
            setLockedUntil(0);
            persistAuthSession(response.data);
            await refreshSessionContext().catch(() => {});

            let shouldCheckout = wantCheckout;
            try {
                if (sessionStorage.getItem('acompanha_pending_mp_checkout') === '1') {
                    shouldCheckout = true;
                    sessionStorage.removeItem('acompanha_pending_mp_checkout');
                }
            } catch (_) {
                // ignore
            }
            if (!shouldCheckout) {
                const stored = (() => {
                    try {
                        return JSON.parse(localStorage.getItem('user') || '{}');
                    } catch {
                        return {};
                    }
                })();
                shouldCheckout = Boolean(stored?.plan_migration_pending);
            }
            if (shouldCheckout) {
                await startSubscriptionCheckoutAfterMigrate(null, () => navigate('/admin'));
                return;
            }
            navigate('/admin');
        } catch (err) {
            const status = err?.response?.status;
            const serverMessage = err?.response?.data?.error || err?.response?.data?.message;
            if (status === 429) {
                const until = Date.now() + LOCAL_LOCK_MS;
                writeLoginLock({ failures: MAX_LOCAL_FAILURES, lockedUntil: until });
                setLockedUntil(until);
                setError(serverMessage || 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.');
            } else {
                registerLocalFailure();
            }
            setIsSubmitting(false);
        }
    };

    const lockSeconds = Math.ceil(lockRemainingMs / 1000);
    void lockTick;

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                backgroundImage: 'url(/images/3D_banner_1.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    zIndex: 1,
                }}
            />

            <Paper
                elevation={3}
                sx={{
                    zIndex: 2,
                    padding: 4,
                    width: '90%',
                    maxWidth: 500,
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                }}
            >
                <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Box
                        component="img"
                        src="/images/logo-acompanha-transp.png"
                        alt="Acompanha Logo"
                        sx={{ width: '180px', height: 'auto', display: 'inline-block' }}
                    />
                </Typography>
                <form onSubmit={handleLogin}>
                    <TextField
                        label="Email"
                        type="email"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLocked || isSubmitting}
                        sx={{
                            backgroundColor: '#f9f9f9',
                            '& .MuiInputLabel-root': { color: '#666666' },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#cccccc' },
                                '&:hover fieldset': { borderColor: '#999999' },
                            },
                        }}
                    />
                    <TextField
                        label="Senha"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={isLocked || isSubmitting}
                        sx={{
                            backgroundColor: '#f9f9f9',
                            '& .MuiInputLabel-root': { color: '#666666' },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#cccccc' },
                                '&:hover fieldset': { borderColor: '#999999' },
                            },
                        }}
                    />

                    {(error || isLocked) && (
                        <Typography color="error" variant="body2" gutterBottom>
                            {isLocked
                                ? `Login temporariamente bloqueado. Tente novamente em ${lockSeconds}s.`
                                : error}
                        </Typography>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                name="rememberMe"
                                color="primary"
                                disabled={isLocked || isSubmitting}
                            />
                        }
                        label="Permanecer logado"
                        sx={{ marginTop: 1 }}
                    />
                    <br />
                    <small>Sua sessão ficará ativa por mais tempo.</small>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        onClick={handleLogin}
                        disabled={isSubmitting || isLocked}
                        sx={{
                            marginTop: 2,
                            background: isSubmitting || isLocked
                                ? 'linear-gradient(to right, #6c757d, #495057)'
                                : 'linear-gradient(to right, #007bff, #0056b3)',
                            color: '#fff',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            padding: '10px 0',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            '&:hover': {
                                background: isSubmitting || isLocked
                                    ? 'linear-gradient(to right, #6c757d, #495057)'
                                    : 'linear-gradient(to right, #0056b3, #003d7a)',
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                            },
                        }}
                    >
                        {isSubmitting ? 'Entrando...' : isLocked ? `Aguarde ${lockSeconds}s` : 'Entrar'}
                    </Button>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            marginTop: 2,
                        }}
                    >
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/admin/forgot-password')}
                            sx={{
                                color: '#007bff',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#0056b3',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Esqueci Minha Senha
                        </Link>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/')}
                            sx={{
                                color: '#007bff',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#0056b3',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Voltar ao Site
                        </Link>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            marginTop: 2,
                        }}
                    >
                        <Button
                            onClick={() => setIsRegistrationModalOpen(true)}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                color: '#007bff',
                                borderColor: '#007bff',
                                '&:hover': {
                                    backgroundColor: '#f0f8ff',
                                    borderColor: '#0056b3',
                                },
                            }}
                        >
                            Realizar Cadastro
                        </Button>
                        </Box>
                </form>
            </Paper>

            <RegistrationModal
                open={isRegistrationModalOpen}
                onClose={() => setIsRegistrationModalOpen(false)}
            />
        </Box>
    );
}

export default LoginPage;
